import { useEffect } from "react";
import { z } from "zod";
import { zStringBoolean } from "@App/hooks/reactRouter/urlSchemas";
import { useSafeSearchParams } from "@App/hooks/reactRouter/useSafeSearchParams";

const SESSION_STORAGE_KEY = "opsBypass";

const schema = z.object({
  ops: zStringBoolean.optional(),
});

/**
 * Reads the `ops` query parameter that lets customer service past a whitelabel home page block
 * (`?ops=true` grants the bypass, `?ops=false` revokes it) and persists it to sessionStorage, so
 * it survives a reload or a browser-back to the home page within the same tab.
 *
 * The URL always wins over the stored value, and the result is derived synchronously — resolving
 * it in an effect would render the blocked state for one paint before the bypass applied.
 *
 * Not a security boundary: the flag is guessable by design, because the block it lifts is a
 * business rule and the deeper routes (`/werkstaetten` and below) were never blocked.
 *
 * Currently used by the FINN home blocker only.
 */
export const useOpsBypassParameter = (): boolean => {
  const { ops } = useSafeSearchParams(schema);

  const bypassed = ops ?? sessionStorage.getItem(SESSION_STORAGE_KEY) === "true";

  useEffect(() => {
    if (ops === undefined) return;

    if (ops) {
      sessionStorage.setItem(SESSION_STORAGE_KEY, "true");
    } else {
      sessionStorage.removeItem(SESSION_STORAGE_KEY);
    }
  }, [ops]);

  return bypassed;
};
